import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import {
  addCountry,
  deleteCountry,
  fetchCountries,
  fetchCountryDetails,
  updateCountry,
} from '../../common/api/countriesService';
import { RootState } from '../../app/store';
import { LOADING_STATUS } from '../../common/constants';
import { TranslationItem } from '../../common/types/translations.types';
import {
  InitialPaginationResponse,
  PaginationParameters,
  PaginationResponse,
  PopulatePagination,
} from '../../common/types/pagination.types';

export interface CountryDetails {
  id?: string;
  isDeleted?: boolean;
  nameTranslations: TranslationItem[];
}

export interface CountryListItem {
  id: string;
  name: string;
}

export interface CountriesFilter extends PaginationParameters {
  searchString: string;
  culture: string;
}

export interface CountriesState {
  countryItems: CountryListItem[];
  countryDetails: CountryDetails;
  countryItemsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  countryDetailsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  countriesPagination: PaginationResponse;
}

const initialState: CountriesState = {
  countryItems: [],
  countryDetails: {
    id: '',
    isDeleted: false,
    nameTranslations: [],
  },
  countryItemsStatus: LOADING_STATUS.IDLE,
  countryDetailsStatus: LOADING_STATUS.IDLE,
  countriesPagination: InitialPaginationResponse,
};

export const getCountriesAsync = createAsyncThunk(
  'countries/getCountries',
  async (countriesFilter: CountriesFilter, { rejectWithValue }) => {
    try {
      const response = await fetchCountries(countriesFilter);
      return response;
    } catch (error) {
      return rejectWithValue(error.response);
    }
  },
);

export const getCountryDetailsAsync = createAsyncThunk(
  'countries/getCountryDetails',
  async (countryId: string, { rejectWithValue }) => {
    try {
      const response = await fetchCountryDetails(countryId);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const saveCountryAsync = createAsyncThunk(
  'countries/addCountry',
  async (countryData: CountryDetails, { rejectWithValue }) => {
    try {
      const response = await addCountry(countryData);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const updateCountryAsync = createAsyncThunk(
  'countries/updateCountry',
  async (countryData: CountryDetails, { rejectWithValue }) => {
    try {
      const response = await updateCountry(countryData);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const deleteCountryAsync = createAsyncThunk(
  'countries/deleteCountry',
  async (countryId: string, { rejectWithValue }) => {
    try {
      const response = await deleteCountry(countryId);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

const countriesSlice = createSlice({
  name: 'countries',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getCountriesAsync.pending, state => {
        state.countryItemsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getCountriesAsync.fulfilled, (state, action) => {
        state.countryItemsStatus = LOADING_STATUS.IDLE;
        state.countryItems = action.payload.data;
        state.countriesPagination = PopulatePagination(action.payload);
      })
      .addCase(getCountriesAsync.rejected, state => {
        state.countryItemsStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getCountryDetailsAsync.pending, state => {
        state.countryDetailsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getCountryDetailsAsync.fulfilled, (state, action) => {
        state.countryDetailsStatus = LOADING_STATUS.IDLE;
        state.countryDetails = action.payload;
      })
      .addCase(getCountryDetailsAsync.rejected, state => {
        state.countryDetailsStatus = LOADING_STATUS.FAILED;
      });
  },
});

export const selectCountryItems = createSelector(
  (state: RootState) => state.countries.countryItems,
  countryItems =>
    countryItems.slice().sort((a, b) => a.name.localeCompare(b.name)),
);
export const selectCountryPagination = (state: RootState) =>
  state.countries.countriesPagination;
export const selectCountryItemsLoadingStatus = (state: RootState) =>
  state.countries.countryItemsStatus;
export const selectCountryDetails = (state: RootState) =>
  state.countries.countryDetails;
export const selectCountryDetailsLoadingStatus = (state: RootState) =>
  state.countries.countryDetailsStatus;

export default countriesSlice.reducer;
